/**
 * Credits used this billing period, against what the plan includes.
 *
 * Shown on the Overview, where usage is read, and in Billing's current plan card, where the number
 * that prompts an upgrade sits beside the plans to upgrade to. Takes plain values: both pages load
 * the usage summary already, so the meter asks for nothing itself.
 */
import { cn } from '@urlshot/ui';

/**
 * From this share of the period's credits the meter turns amber, and the Overview's upgrade banner
 * says the limit is near. One constant, so the two cannot change at different points.
 */
export const NEARLY_USED = 0.8;

export function CreditMeter({
  used,
  included,
  resetsAt,
  'data-testid': testId,
}: {
  used: number;
  included: number;
  /** When the period ends and the credits reset. */
  resetsAt: string;
  'data-testid'?: string;
}) {
  if (included <= 0) return null;

  // Capped: a period can end over its allowance (a render in flight when the last credit went), and
  // a bar wider than its track would say nothing more.
  const share = Math.min(used / included, 1);
  // One decimal is finer than a pixel on this bar, and keeps floating-point noise out of the markup.
  const widthPercent = Math.round(share * 1000) / 10;
  const fill = share >= 1 ? 'bg-red-600' : share >= NEARLY_USED ? 'bg-amber-500' : 'bg-brand-400';
  const resetsOn = new Date(resetsAt).toLocaleDateString('en-US', { dateStyle: 'long' });

  return (
    <div className="max-w-md" data-testid={testId}>
      <p className="text-sm text-neutral-700">
        {used.toLocaleString('en-US')} of {included.toLocaleString('en-US')} credits used · resets
        on {resetsOn}
      </p>
      <div
        role="progressbar"
        aria-label="Credits used this period"
        aria-valuemin={0}
        aria-valuemax={included}
        aria-valuenow={Math.min(used, included)}
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-200"
      >
        <div
          className={cn('h-full rounded-full', fill)}
          style={{ width: `${String(widthPercent)}%` }}
        />
      </div>
    </div>
  );
}
